import { useT } from "@/i18n";
import { Select } from "@/components/ui/input";
import type { usePipelineScope } from "./usePipelineScope";

/**
 * Which year the pipeline is looking at — a scope, not a filter, so it sits in the page
 * header rather than the filter panel, and it never becomes a dismissable chip.
 *
 * The options come from the server: "which year is it" computed in a browser disagrees with
 * the server on 31 December, and the two archive years in front of the editable window used
 * to be arithmetic inside the board's own header, where the list could not see it.
 */
export function YearScope({ scope }: { scope: ReturnType<typeof usePipelineScope> }): React.JSX.Element {
  const t = useT();
  const years = scope.selectableYears;

  return (
    <Select
      aria-label={t("year.label")}
      // Empty while the window loads, so the control never offers a year the server has not
      // confirmed — a placeholder year would be a guess rendered as a fact.
      value={scope.year === undefined ? "" : String(scope.year)}
      disabled={!years}
      onChange={(event) => scope.setYear(Number(event.target.value))}
      className="h-9 w-28"
    >
      {years ? (
        // The year alone. An "(archive)" suffix widened the control by more than it
        // explained — a past year *is* the archive, and the board's own read-only state says
        // so where it matters. `year.archive` is kept as the accessible description below.
        years.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))
      ) : (
        <option value="">{t("common.loading")}</option>
      )}
    </Select>
  );
}
